import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import { Button } from '../components/Button';

export function Terms(_props: RoutableProps) {
  return (
    <div className="min-h-screen bg-surface-50">
      <div className="container-custom py-8">
        <div className="max-w-2xl mx-auto">
          <header className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold text-neutral-900">Terms of Service</h1>
            <Button onClick={() => route('/')} variant="ghost" size="sm">
              Home
            </Button>
          </header>

          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="prose prose-neutral max-w-none">
              <p className="text-sm text-neutral-500 mb-6">Last updated: January 2026</p>

              <h2>Welcome to Storyseed</h2>
              <p>
                These Terms of Service ("Terms") govern your use of Storyseed, a web application
                that creates personalized stories for children using artificial intelligence. By
                using Storyseed, you agree to these Terms.
              </p>

              <h2>Who Can Use Storyseed</h2>
              <ul>
                <li>
                  <strong>Parents and Guardians:</strong> Adults (18+) may use Storyseed and
                  authorize their children to use it under supervision
                </li>
                <li>
                  <strong>Children:</strong> Children under 13 may use Storyseed only with parental
                  consent and supervision, as required by COPPA
                </li>
                <li>
                  <strong>Educators:</strong> Teachers and librarians may use Storyseed with
                  students with appropriate authorization
                </li>
              </ul>

              <h2>The Service</h2>

              <h3>What Storyseed Does</h3>
              <p>
                Storyseed uses AI (specifically Anthropic's Claude) to generate personalized
                children's stories based on user choices (animal character, name, story type, and
                setting). Stories are designed to be age-appropriate for children ages 5-10.
              </p>

              <h3>Pricing</h3>
              <p>
                <strong>Storyseed charges $1.99 per story.</strong> Each story includes the
                AI-generated text, professional voice narration, and custom illustrations. The
                price is clearly displayed before you begin creating a story. Payment is processed
                securely through our payment provider after story generation. No subscriptions or
                hidden fees.
              </p>

              <h3>AI-Generated Content</h3>
              <p>All stories are generated by artificial intelligence. This means:</p>
              <ul>
                <li>Each story is unique and created in real-time</li>
                <li>
                  We cannot guarantee specific content, though we use strict safety guidelines
                </li>
                <li>
                  AI output may occasionally be imperfect despite our content filters
                </li>
                <li>Parents should review stories, especially for younger children</li>
              </ul>

              <h2>Content Guidelines</h2>

              <h3>Our Commitments</h3>
              <p>We design our AI prompts to ensure stories:</p>
              <ul>
                <li>Are age-appropriate for children ages 5-10</li>
                <li>Contain no violence, scary content, or adult themes</li>
                <li>Use positive, encouraging language</li>
                <li>Include gentle moral lessons appropriate for children</li>
              </ul>

              <h3>Your Responsibilities</h3>
              <ul>
                <li>Use appropriate names and choices when creating stories</li>
                <li>Do not attempt to manipulate the service to generate inappropriate content</li>
                <li>Review stories with or before your child when appropriate</li>
                <li>Report any concerning content to us immediately</li>
              </ul>

              <h2>Acceptable Use</h2>
              <p>You agree NOT to:</p>
              <ul>
                <li>Use Storyseed for any illegal purpose</li>
                <li>Attempt to generate inappropriate, harmful, or adult content</li>
                <li>Abuse, harass, or harm others through the service</li>
                <li>Circumvent rate limits or security measures</li>
                <li>Use automated tools to access the service excessively</li>
                <li>Reverse engineer or attempt to extract the underlying AI prompts</li>
                <li>Resell or commercially redistribute generated content without permission</li>
              </ul>

              <h2>Intellectual Property</h2>

              <h3>Your Content</h3>
              <p>
                Stories generated for you are yours to keep, share with family, and use for
                personal, non-commercial purposes. You may print stories, share links, and read
                them aloud.
              </p>

              <h3>Our Service</h3>
              <p>
                The Storyseed name, logo, website design, and underlying technology remain our
                property. You may not copy, modify, or distribute our service or brand assets.
              </p>

              <h2>Disclaimers</h2>

              <h3>Service Availability</h3>
              <p>
                Storyseed is provided "as is." We strive for reliability but cannot guarantee
                uninterrupted service. We may modify, suspend, or discontinue features at any time.
              </p>

              <h3>Content Accuracy</h3>
              <p>
                AI-generated stories are creative fiction. We do not guarantee factual accuracy of
                any educational content within stories. Stories are meant for entertainment and
                should not be relied upon for educational instruction.
              </p>

              <h3>Limitation of Liability</h3>
              <p>
                To the fullest extent permitted by law, Storyseed and its operators shall not be
                liable for any indirect, incidental, or consequential damages arising from your use
                of the service.
              </p>

              <h2>Privacy</h2>
              <p>
                Your privacy is important to us, especially regarding children's data. Please
                review our{' '}
                <a href="/privacy" className="text-primary-600">
                  Privacy Policy
                </a>{' '}
                for details on how we collect, use, and protect information.
              </p>

              <h2>Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. We will notify users of significant
                changes by updating the "Last updated" date. Continued use after changes
                constitutes acceptance of the new Terms.
              </p>

              <h2>Termination</h2>
              <p>
                You may stop using Storyseed at any time. We reserve the right to suspend or
                terminate access for users who violate these Terms or abuse the service.
              </p>

              <h2>Contact</h2>
              <p>
                For questions about these Terms of Service:
                <br />
                <strong>Email:</strong>{' '}
                <a href="mailto:support@storyseed.app">support@storyseed.app</a>
              </p>

              <h2>Governing Law</h2>
              <p>
                These Terms are governed by the laws of the United States. Any disputes shall be
                resolved in accordance with applicable law.
              </p>

              <div className="mt-8 pt-6 border-t border-neutral-200">
                <p className="text-sm text-neutral-500">
                  By using Storyseed, you acknowledge that you have read, understood, and agree to
                  be bound by these Terms of Service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
